/**
 * WidgetRenderer Component
 * Main component that renders the appropriate content for each widget type
 */

import { useMemo, useCallback } from 'react';
import { WIDGET_TYPES } from './widgetConfig';
import { WidgetWrapper } from './WidgetWrapper';
import {
  CustomTextContent,
  CustomMetricContent,
  CustomApiMetricContent,
  CustomProgressContent,
  CustomChecklistContent,
  CustomCountdownContent,
  CustomNotesContent,
  QuickLinksContent,
  ImageWidgetContent,
  PowerBIReportContent,
  EmbedWebsiteContent,
  JiraDashboardSnapshotContent,
  AzureCostWidgetContent,
  M365LicensesWidgetContent,
  EntraIDUsersWidgetContent,
  SecurityPostureWidgetContent,
  M365ServiceHealthWidgetContent,
  StorageUsageWidgetContent,
  TenantInfoWidgetContent,
  M365OverviewWidgetContent,
  ProjectsWidgetContent,
  InternalServicesWidgetContent,
  ExternalServicesWidgetContent,
  JiraOperationsWidgetContent,
  GoalsTrackerWidgetContent,
  ExpensesWidgetContent,
} from './WidgetContent';
import { ExecutiveCardWidget } from './ExecutiveCardWidget';
import { DashboardLinkWidget } from './DashboardLinkWidget';

/**
 * WidgetRenderer - Renders a single widget with its content
 */
export const WidgetRenderer = ({
  widget,
  isEditMode,
  onRemove,
  onConfigure,
  onUpdateWidgetConfig,
  timeRange = '30 days',
}) => {
  const { type, i: widgetId, config: widgetConfig } = widget;

  // Handle config changes from within widgets
  const handleConfigChange = useCallback((newConfig) => {
    if (onUpdateWidgetConfig) {
      onUpdateWidgetConfig(widgetId, newConfig);
    }
  }, [widgetId, onUpdateWidgetConfig]);

  // Memoize widget content to prevent unnecessary re-renders
  const content = useMemo(() => {
    switch (type) {
      // === Content Widgets ===
      case WIDGET_TYPES.CUSTOM_TEXT:
        return (
          <CustomTextContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.CUSTOM_NOTES:
        return (
          <CustomNotesContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.IMAGE_WIDGET:
        return (
          <ImageWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      // === Metric Widgets ===
      case WIDGET_TYPES.CUSTOM_METRIC:
        return (
          <CustomMetricContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.CUSTOM_PROGRESS:
        return (
          <CustomProgressContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.CUSTOM_API_METRIC:
        return (
          <CustomApiMetricContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.AZURE_COST_WIDGET:
        return (
          <AzureCostWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
            timeRange={timeRange}
          />
        );

      case WIDGET_TYPES.EXECUTIVE_CARD_WIDGET:
        return (
          <ExecutiveCardWidget
            config={widgetConfig}
            onConfigure={() => onConfigure && onConfigure(widgetId)}
          />
        );

      case WIDGET_TYPES.DASHBOARD_LINK_WIDGET:
        return (
          <DashboardLinkWidget
            config={widgetConfig}
            onConfigure={() => onConfigure && onConfigure(widgetId)}
          />
        );

      // === Microsoft 365 Widgets ===
      case WIDGET_TYPES.M365_OVERVIEW_WIDGET:
        return (
          <M365OverviewWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.MS_LICENSES_WIDGET:
        return (
          <M365LicensesWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.MS_USERS_WIDGET:
        return (
          <EntraIDUsersWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.SECURITY_POSTURE_WIDGET:
        return (
          <SecurityPostureWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.SERVICE_HEALTH_WIDGET:
        return (
          <M365ServiceHealthWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.STORAGE_USAGE_WIDGET:
        return (
          <StorageUsageWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.TENANT_INFO_WIDGET:
        return (
          <TenantInfoWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      // === IT Operations Widgets ===
      case WIDGET_TYPES.PROJECTS_WIDGET:
        return (
          <ProjectsWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.INTERNAL_SERVICES_WIDGET:
        return (
          <InternalServicesWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.EXTERNAL_SERVICES_WIDGET:
        return (
          <ExternalServicesWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.JIRA_OPERATIONS_WIDGET:
        return (
          <JiraOperationsWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.GOALS_TRACKER_WIDGET:
        return (
          <GoalsTrackerWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.EXPENSES_WIDGET:
        return (
          <ExpensesWidgetContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      // === Productivity Widgets ===
      case WIDGET_TYPES.CUSTOM_CHECKLIST:
        return (
          <CustomChecklistContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.CUSTOM_COUNTDOWN:
        return (
          <CustomCountdownContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.QUICK_LINKS:
        return (
          <QuickLinksContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      // === Embed Widgets ===
      case WIDGET_TYPES.POWERBI_REPORT:
        return (
          <PowerBIReportContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.EMBED_WEBSITE:
        return (
          <EmbedWebsiteContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      case WIDGET_TYPES.JIRA_DASHBOARD_SNAPSHOT:
        return (
          <JiraDashboardSnapshotContent
            config={widgetConfig}
            onConfigChange={handleConfigChange}
          />
        );

      default:
        return (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            Unknown widget type: {type}
          </div>
        );
    }
  }, [type, widgetConfig, handleConfigChange]);

  return (
    <WidgetWrapper
      widgetId={widgetId}
      widgetType={type}
      isEditMode={isEditMode}
      onRemove={onRemove}
      onConfigure={onConfigure}
    >
      {content}
    </WidgetWrapper>
  );
};

export default WidgetRenderer;
